/**
 * Table with configurable columns and load-more.
 * Visible rows are driven by parent (limit/load-more); columns use valueKey to read from ReportWidgetItem.
 */
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportWidgetStickyHeader } from './table/ReportWidgetStickyHeader';
import { ReportWidgetTableCell } from './table/ReportWidgetTableCell';
import { ReportWidgetTableHeaderCell } from './table/ReportWidgetTableHeaderCell';
import { ReportWidgetTableLoading } from './table/ReportWidgetTableLoading';

import { useHorizontalTableScroll } from '../model/hooks/useHorizontalTableScroll';
import { useReportWidgetStickyHeader } from '../model/hooks/useReportWidgetStickyHeader';

import type {
    ReportWidgetColumnConfig,
    ReportWidgetItem,
    ReportWidgetTableOptions,
} from '../model/types';

function getColumnFallbackMinWidth(col: ReportWidgetColumnConfig): number {
    if (col.format === 'currency') return 112;
    if (col.format === 'number') return 84;
    if (col.format === 'percent') return 84;
    return 96;
}

interface ReportWidgetTableProps {
    items: ReportWidgetItem[];
    columns: ReportWidgetColumnConfig[];
    /** Presentation options (labels, show details column). Aligns with config + derived state. */
    options?: ReportWidgetTableOptions;
    /** Resolved colors per row (same order as items), for swatch in label column. */
    itemColors?: string[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    onRowClick?: (item: ReportWidgetItem) => void;
    onRowHover?: (item: ReportWidgetItem | null) => void;
    onDetailsClick?: (item: ReportWidgetItem) => void;
    onLoadMoreHover?: (hovering: boolean) => void;
}

export const ReportWidgetTable = memo(
    ({
        items,
        columns,
        options,
        itemColors = [],
        loading = false,
        loadingMore = false,
        hasMore = false,
        onLoadMore,
        onRowClick,
        onRowHover,
        onDetailsClick,
        onLoadMoreHover,
    }: ReportWidgetTableProps) => {
        const { t } = useTranslation();
        const {
            containerRef: tableContainerRef,
            hasHorizontalOverflow,
            isAtScrollStart,
            isAtScrollEnd,
            handleScroll: updateHorizontalOverflowState,
            refresh: refreshHorizontalOverflowState,
        } = useHorizontalTableScroll();
        const {
            loadMoreLabel,
            loadMoreVariant = 'button',
            loadMoreCount = 0,
            showColorSwatchInLabelColumn,
            emptyMessage,
        } = options ?? {};
        const tableMinWidth = useMemo(
            () =>
                columns.reduce(
                    (sum, col) =>
                        sum + (col.minWidth ?? getColumnFallbackMinWidth(col)),
                    0,
                ),
            [columns],
        );

        useEffect(() => {
            refreshHorizontalOverflowState();
        }, [items, columns, refreshHorizontalOverflowState]);

        const {
            bodyTableRef,
            stickyHeaderInnerRef,
            headerColumnWidths,
            headerTableWidth,
            headerHeight,
            handleTableScroll,
        } = useReportWidgetStickyHeader({
            columns,
            itemsLength: items.length,
            tableMinWidth,
            scrollContainerRef: tableContainerRef,
            onTableScrolled: updateHorizontalOverflowState,
        });

        if (loading) {
            return <ReportWidgetTableLoading />;
        }

        return (
            <Box
                data-testclass="composable-report-widget-table"
                sx={{ width: '100%', minWidth: 0 }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <ReportWidgetStickyHeader
                        headerHeight={headerHeight}
                        headerTableWidth={headerTableWidth}
                        tableMinWidth={tableMinWidth}
                        stickyHeaderInnerRef={stickyHeaderInnerRef}
                    >
                        {columns.map((col, index) => (
                            <ReportWidgetTableHeaderCell
                                key={col.id}
                                column={col}
                                measuredWidth={headerColumnWidths[index]}
                            />
                        ))}
                    </ReportWidgetStickyHeader>
                    <TableContainer
                        ref={tableContainerRef}
                        onScroll={handleTableScroll}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            overflowX: 'auto',
                            overflowY: 'visible',
                        }}
                    >
                        <Table
                            ref={bodyTableRef}
                            size="medium"
                            sx={{
                                width: '100%',
                                minWidth: `${tableMinWidth}px`,
                            }}
                        >
                            <TableHead
                                sx={{
                                    '& .MuiTableCell-head': {
                                        backgroundColor: 'transparent',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    },
                                }}
                            >
                                <TableRow>
                                    {columns.map((col) => (
                                        <ReportWidgetTableHeaderCell
                                            key={col.id}
                                            column={col}
                                        />
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            sx={{
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                padding: 3,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {emptyMessage ??
                                                    t(
                                                        'composableReportWidget.noData',
                                                    )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map((item, rowIndex) => (
                                        <TableRow
                                            key={item.id}
                                            hover={Boolean(
                                                onRowClick || onRowHover,
                                            )}
                                            onClick={() => onRowClick?.(item)}
                                            onMouseEnter={() =>
                                                onRowHover?.(item)
                                            }
                                            onMouseLeave={() =>
                                                onRowHover?.(null)
                                            }
                                            sx={{
                                                cursor:
                                                    onRowClick || onRowHover
                                                        ? 'pointer'
                                                        : 'default',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            {columns.map((col) => (
                                                <ReportWidgetTableCell
                                                    key={col.id}
                                                    item={item}
                                                    column={col}
                                                    showColorSwatchInLabelColumn={
                                                        showColorSwatchInLabelColumn
                                                    }
                                                    swatchColor={
                                                        itemColors[rowIndex] ??
                                                        item.color
                                                    }
                                                    onLabelClick={
                                                        col.isDetailsTrigger &&
                                                        onDetailsClick
                                                            ? () =>
                                                                  onDetailsClick(
                                                                      item,
                                                                  )
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {hasHorizontalOverflow && !isAtScrollStart && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                width: 32,
                                pointerEvents: 'none',
                                background:
                                    'linear-gradient(to right, rgba(255,255,255,0.98), rgba(255,255,255,0))',
                            }}
                        />
                    )}
                    {hasHorizontalOverflow && !isAtScrollEnd && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                right: 0,
                                width: 32,
                                pointerEvents: 'none',
                                background:
                                    'linear-gradient(to left, rgba(255,255,255,0.98), rgba(255,255,255,0))',
                            }}
                        />
                    )}
                </Box>
                {(hasMore || loadingMore) && onLoadMore && (
                    <Box
                        sx={{
                            paddingTop: 2,
                            paddingBottom: 1,
                            paddingLeft: 0,
                            paddingRight: 0,
                            display: 'flex',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minHeight: 32,
                            }}
                        >
                            {loadMoreVariant === 'link' ? (
                                <Typography
                                    component="button"
                                    variant="body2"
                                    onClick={
                                        loadingMore ? undefined : onLoadMore
                                    }
                                    data-testclass="composable-report-widget-load-more"
                                    onMouseEnter={() => onLoadMoreHover?.(true)}
                                    onMouseLeave={() =>
                                        onLoadMoreHover?.(false)
                                    }
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: loadingMore
                                            ? 'default'
                                            : 'pointer',
                                        border: 'none',
                                        background: 'none',
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        font: 'inherit',
                                        padding: 0,
                                        fontSize: '0.8125rem',
                                        opacity: loadingMore ? 0.7 : 1,
                                        '&:hover': {
                                            color: loadingMore
                                                ? 'text.secondary'
                                                : 'text.primary',
                                        },
                                    }}
                                >
                                    {loadingMore ? (
                                        <CircularProgress
                                            size={16}
                                            sx={{ marginRight: 0.5 }}
                                        />
                                    ) : (
                                        <ChevronDownIcon
                                            sx={{ fontSize: 18 }}
                                        />
                                    )}
                                    {loadMoreLabel != null
                                        ? loadMoreLabel.replace(
                                              '{{count}}',
                                              String(loadMoreCount),
                                          )
                                        : t(
                                              'composableReportWidget.showMoreLink',
                                              {
                                                  count: loadMoreCount,
                                              },
                                          )}
                                </Typography>
                            ) : (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onLoadMore}
                                    disabled={loadingMore}
                                    data-testclass="composable-report-widget-load-more"
                                    startIcon={
                                        loadingMore ? (
                                            <CircularProgress size={16} />
                                        ) : undefined
                                    }
                                >
                                    {loadMoreLabel ??
                                        t('composableReportWidget.loadMore')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    },
);

ReportWidgetTable.displayName = 'ReportWidgetTable';
